import React, { BaseSyntheticEvent, useContext, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Typography,
} from '@dls/react-core';
import { FileUpload } from '@dls/react-file-upload';
import { StudyPageContext } from '../../App';
import { importData } from '../../api/studykit';
import { useSnackbar } from 'notistack';
import { NotiStackAction } from '../styled/notistack-action';
import { useSubjectContext } from '../../pages/subject';

interface ImportDataProps {
  open: boolean;
  onClose: () => void;
}

export const ImportData: React.FC<ImportDataProps> = ({ open, onClose }) => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { subject } = useSubjectContext();
  const { study } = useContext(StudyPageContext);

  const handleDrop = (newFiles: File[]) => {
    setAcceptedFiles((previousFiles: any) => [...previousFiles, ...newFiles]);
  };

  const handleDelete = (file: { path: any }) => {
    setAcceptedFiles(acceptedFiles.filter((e: any) => e.path !== file.path));
  };

  const resetAndClose = () => {
    setAcceptedFiles([]);
    onClose();
  };

  const onSave = (e: BaseSyntheticEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    importData(study.id, subject.id, acceptedFiles)
      .then((r) => {
        for (const importResult of r) {
          if (importResult.result === 'failed') {
            enqueueSnackbar(
              <p>
                <strong>Failed: {importResult.fileName}</strong> <br />
                {importResult.data.error.message}
              </p>,
              {
                autoHideDuration: 6000,
                variant: 'error',
                action: <NotiStackAction close={closeSnackbar} />,
              },
            );
          } else {
            enqueueSnackbar(`Success: ${importResult.fileName}`, {
              variant: 'success',
              action: <NotiStackAction close={closeSnackbar} />,
            });
          }
        }

        if (r.every((r) => r.result === 'success')) {
          resetAndClose();
        }
      })
      .finally(() => setIsSubmitting(false));
  };
  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      PaperProps={{ style: { width: `50vw`, maxWidth: `600px` } }}
    >
      <DialogTitle disableTypography>
        <Typography variant="body2" weight={'bold'}>
          Import Metric Data
        </Typography>
      </DialogTitle>
      <Divider orientation={'horizontal'} />
      <DialogContent>
        {isSubmitting && <LinearProgress variant={'indeterminate'} />}
        <FileUpload
          type={'dropzone'}
          onFileDrop={handleDrop}
          onDelete={handleDelete}
          files={acceptedFiles}
          maxFilesize={256_000_000}
          accept={'.txt,.zip'}
          text={'Browse'}
          disabled={false}
          dropvalues={
            <>
              <Typography variant={'body2'}>Drag and drop file(s)</Typography>
              <Typography variant={'body2'}>Or</Typography>
            </>
          }
        />
      </DialogContent>

      <Divider orientation={'horizontal'} />
      <DialogActions style={{ justifyContent: `space-between` }}>
        <Button
          style={{ width: `8rem` }}
          disabled={isSubmitting}
          onClick={resetAndClose}
          dls_variant={'quietDefault'}
        >
          Cancel
        </Button>
        <Button
          disabled={isSubmitting || !acceptedFiles.length}
          onClick={onSave}
          dls_variant={'primary'}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};
